import { useEffect, useRef } from 'react';

const HUES = ['rgba(34,211,238,', 'rgba(139,92,246,', 'rgba(16,185,129,', 'rgba(225,29,72,'];

export default function Starfield({ className = '' }) {
  const canvasRef = useRef(null);
  const mouse = useRef({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let raf;
    let w, h;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const stars = [];
    const organisms = [];

    const resize = () => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const seed = () => {
      stars.length = 0;
      organisms.length = 0;
      const count = Math.min(220, Math.floor((w * h) / 9000));
      for (let i = 0; i < count; i++) {
        stars.push({
          x: Math.random() * w,
          y: Math.random() * h,
          r: Math.random() * 1.4 + 0.3,
          hue: HUES[Math.floor(Math.random() * 3)],
          tw: Math.random() * Math.PI * 2,
          ts: 0.4 + Math.random() * 1.2,
          depth: 0.3 + Math.random() * 0.7,
        });
      }
      for (let i = 0; i < 7; i++) {
        organisms.push({
          x: Math.random() * w,
          y: Math.random() * h,
          r: 60 + Math.random() * 110,
          hue: HUES[Math.floor(Math.random() * HUES.length)],
          vx: (Math.random() - 0.5) * 0.12,
          vy: (Math.random() - 0.5) * 0.1,
          pulse: Math.random() * Math.PI * 2,
        });
      }
    };

    const onMouse = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.current = { x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height };
    };

    const draw = (t) => {
      ctx.clearRect(0, 0, w, h);
      const px = (mouse.current.x - 0.5) * 24;
      const py = (mouse.current.y - 0.5) * 24;

      for (const o of organisms) {
        o.x += o.vx;
        o.y += o.vy;
        o.pulse += 0.008;
        if (o.x < -o.r) o.x = w + o.r;
        if (o.x > w + o.r) o.x = -o.r;
        if (o.y < -o.r) o.y = h + o.r;
        if (o.y > h + o.r) o.y = -o.r;
        const breathe = 0.75 + Math.sin(o.pulse) * 0.25;
        const g = ctx.createRadialGradient(o.x + px * 0.4, o.y + py * 0.4, 0, o.x + px * 0.4, o.y + py * 0.4, o.r * breathe);
        g.addColorStop(0, `${o.hue}0.10)`);
        g.addColorStop(0.5, `${o.hue}0.04)`);
        g.addColorStop(1, `${o.hue}0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(o.x + px * 0.4, o.y + py * 0.4, o.r * breathe, 0, Math.PI * 2);
        ctx.fill();
      }

      for (const s of stars) {
        s.tw += 0.02 * s.ts;
        const alpha = 0.25 + Math.abs(Math.sin(s.tw)) * 0.75;
        ctx.fillStyle = `${s.hue}${alpha * 0.9})`;
        ctx.beginPath();
        ctx.arc(s.x + px * s.depth, s.y + py * s.depth, s.r, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };

    resize();
    seed();
    raf = requestAnimationFrame(draw);
    window.addEventListener('resize', () => { resize(); seed(); });
    canvas.addEventListener('mousemove', onMouse);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', onMouse);
    };
  }, []);

  return <canvas ref={canvasRef} className={className} data-testid="hero-starfield-canvas" />;
}
